import FileXIcon from "@redotech/redo-icons/file-x-02.svg";
import { Carriers } from "@redotech/redo-model/fulfillments/fulfillment-carriers-and-services";
import { FulfillmentGroupData } from "@redotech/redo-model/fulfillments/fulfillment-group";
import { PrintableDocument } from "@redotech/redo-model/fulfillments/print-job";
import { showNotification } from "@redotech/redo-web/alert";
import { NotificationInstance } from "@redotech/redo-web/notification-provider";
import {
  createDocumentPrintStatusMap,
  PrintBatchInput,
} from "./print-batch-modal";

export function showWatchedGroupsNotification({
  rows,
  newRows,
  printBatch,
}: {
  rows: FulfillmentGroupData[];
  newRows: FulfillmentGroupData[];
  printBatch: (input: PrintBatchInput) => void;
}): void {
  for (const newRow of newRows) {
    const oldRow = rows.find((row) => row._id === newRow._id);
    if (shouldShowCommercialInvoiceNotification({ oldRow, newRow })) {
      showNotification(
        getCommercialInvoiceNotificationInstance({
          groupId: newRow._id,
          orderName: newRow?.orders?.[0]?.name,
          printBatch,
        }),
      );
    }
  }
}

function getCommercialInvoiceNotificationInstance({
  groupId,
  orderName,
  printBatch,
}: {
  groupId: string;
  orderName: string | undefined;
  printBatch: (input: PrintBatchInput) => void;
}): NotificationInstance {
  return {
    title: "Your commercial invoice was not submitted electronically",
    description: `A commercial invoice ${orderName ? `associated with order ${orderName} ` : ""}may not have been submitted electronically.`,
    primaryButton: {
      text: "Print commercial invoice",
      onClick: () => {
        printBatch({
          batch: { fulfillmentGroupIds: [groupId] },
          document: PrintableDocument.COMMERCIAL_INVOICE,
          documentPrintStatusMap: createDocumentPrintStatusMap({
            [PrintableDocument.COMMERCIAL_INVOICE]: true,
          }),
        });
      },
    },
    featuredIcon: { Icon: FileXIcon, color: "error" },
  };
}

function shouldShowCommercialInvoiceNotification({
  oldRow,
  newRow,
}: {
  oldRow: FulfillmentGroupData | undefined;
  newRow: FulfillmentGroupData;
}): boolean {
  const carrier = newRow.shipment?.boughtRate?.carrier;
  // APC and ePost Global are EasyPost-routed international postal consolidators that
  // submit customs data electronically on the carrier side, but EasyPost doesn't reliably
  // set submitted_electronically: true on the returned form. Suppress to avoid spurious alerts.
  const carriersToSuppress = [
    Carriers.APC,
    Carriers.EPOST_GLOBAL,
    Carriers.AUSTRALIA_POST,
  ];
  if (carrier && carriersToSuppress.includes(carrier)) {
    return false;
  }

  const oldUrl = oldRow?.shipment?.label?.commercialInvoiceUrl;
  const newUrl = newRow?.shipment?.label?.commercialInvoiceUrl;

  const newSubmittedElectronically: boolean | undefined | null =
    newRow?.shipment?.label?.comInvoiceSubmittedElectronically;

  const urlAdded = oldUrl === undefined && newUrl != undefined;
  const notSubmitted =
    newSubmittedElectronically === false || newSubmittedElectronically == null;

  return urlAdded && notSubmitted;
}
